import FilterDate from '@/Enums/FilterDate.enum';
import DateFormat from '@/Enums/Format/DateFormat.enum';
import { MONTH_PICKER_SHORTCUTS } from '@/constants/date';
import { ArrowDropDown, CalendarMonth } from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Popover,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  outlinedInputClasses,
} from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment, { Moment } from 'moment';
import { MouseEvent, useCallback, useState } from 'react';

interface IProps {
  onChange: (value: DateRange<Moment>) => void;
}

const MonthYearPicker = ({ onChange }: IProps) => {
  const today = moment();
  const previous = moment().subtract(1, 'month');

  const [selectedDate, setSelectedDate] = useState<Moment>(today.startOf('month'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const [tmpYear, setTmpYear] = useState<number>(selectedDate.year());
  const [tmpMonth, setTmpMonth] = useState<number>(selectedDate.month());

  const [shortcutValue, setShortcutValue] = useState<FilterDate>(FilterDate.THIS_MONTH);

  const openPopover = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
      setIsOpen(true);
    },
    [setAnchorEl, setIsOpen],
  );

  const closePopover = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const isMonthDisabled = useCallback(
    (month: number) => {
      return tmpYear === today.year() && month > today.month();
    },
    [tmpYear, today],
  );

  const handleMonthChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      const month = +event.target.value;
      let newShortcutValue = FilterDate.CUSTOM_RANGE;

      if (month === today.month() && tmpYear === today.year()) {
        newShortcutValue = FilterDate.THIS_MONTH;
      }

      if (month === previous.month() && tmpYear === previous.year()) {
        newShortcutValue = FilterDate.LAST_MONTH;
      }

      setTmpMonth(month);
      setShortcutValue(newShortcutValue);
    },
    [tmpYear, setTmpMonth, setShortcutValue, today, previous],
  );

  const handleYearChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      const year = +event.target.value;
      let newShortcutValue = FilterDate.CUSTOM_RANGE;

      if (year === today.year() && tmpMonth === today.month()) {
        newShortcutValue = FilterDate.THIS_MONTH;
      }

      if (year === previous.year() && tmpMonth === previous.month()) {
        newShortcutValue = FilterDate.LAST_MONTH;
      }

      setTmpYear(year);
      setShortcutValue(newShortcutValue);
    },
    [tmpMonth, setTmpYear, setShortcutValue, today, previous],
  );

  const handleShortcut = useCallback(
    (value: FilterDate) => {
      setShortcutValue(value);

      if (value === FilterDate.THIS_MONTH) {
        setTmpMonth(today.month());
        setTmpYear(today.year());
      }

      if (value === FilterDate.LAST_MONTH) {
        setTmpMonth(previous.month());
        setTmpYear(previous.year());
      }
    },
    [setShortcutValue, setTmpMonth, setTmpYear, today, previous],
  );

  const handleApply = useCallback(() => {
    const startDateRange = moment({ year: tmpYear, month: tmpMonth }).startOf('month');
    const endDateRange = moment({ year: tmpYear, month: tmpMonth }).endOf('month');
    onChange([startDateRange, endDateRange]);
    setSelectedDate(startDateRange);
    closePopover();
  }, [tmpMonth, tmpYear, setSelectedDate, closePopover, onChange]);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box>
        <OutlinedInput
          value={selectedDate.format(DateFormat.LongMonthYear)}
          onClick={openPopover}
          readOnly
          startAdornment={<CalendarMonth />}
          endAdornment={<ArrowDropDown />}
          placeholder='Select Month'
          sx={{
            minWidth: '200px',
            height: '45px',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
            background: '#fff',
            [`& .${outlinedInputClasses.input}`]: {
              paddingLeft: '6px',
              paddingRight: 0,
              cursor: 'inherit',
            },
          }}
        />

        <Popover
          open={isOpen}
          anchorEl={anchorEl}
          onClose={closePopover}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{ marginTop: 1 }}
        >
          <Box sx={{ padding: 2, width: '320px' }}>
            <FormControl fullWidth>
              <RadioGroup
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  paddingBottom: '12px',
                }}
                value={shortcutValue}
              >
                {MONTH_PICKER_SHORTCUTS.map(({ value, label }) => (
                  <Radio
                    sx={{
                      borderRadius: '8px',
                      margin: 0,
                      height: '40px',
                      color: 'black',
                      '&.Mui-checked': {
                        backgroundColor: 'rgba(27, 67, 50, 0.12)',
                      },
                    }}
                    value={value}
                    key={value}
                    checkedIcon={<Box>{label}</Box>}
                    icon={<Box>{label}</Box>}
                    onClick={() => handleShortcut(value)}
                  />
                ))}
              </RadioGroup>
            </FormControl>

            <FormControl fullWidth sx={{ marginTop: 1 }}>
              <InputLabel>Month</InputLabel>
              <Select value={tmpMonth} onChange={handleMonthChange} label='Month' displayEmpty>
                {Array.from({ length: 12 }).map((_, idx) => (
                  <MenuItem key={idx} value={idx} disabled={isMonthDisabled(idx)}>
                    {moment().month(idx).format(DateFormat.LongMonth)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ marginTop: 3 }}>
              <InputLabel>Year</InputLabel>
              <Select value={tmpYear} onChange={handleYearChange} label='Year' displayEmpty>
                {Array.from({ length: 10 }).map((_, idx) => {
                  const year = today.year() - idx;
                  return (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <Button
              fullWidth
              color='primary'
              variant='contained'
              sx={{
                marginTop: 3,
                height: 48,
              }}
              onClick={handleApply}
            >
              Apply
            </Button>
          </Box>
        </Popover>
      </Box>
    </LocalizationProvider>
  );
};

export default MonthYearPicker;
